"use client";

import { AttentionBadges } from "@/components/attention-badges";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import {
  LicenseRowActions,
  licenseRowActionsColumnWidth,
} from "@/components/registrant/dashboard/license-row-actions";
import { Badge } from "@/components/ui/badge";
import { UserType } from "@/constants";
import type {
  RegistrantLicenseListLicenseResponseSchema,
  RegistrantLicenseListQuestionColumnSchema,
} from "@/hera-api/client/types.gen";
import { attentionColumnParts } from "@/lib/attention-column";
import {
  compareDateStrings,
  formatDateNoTimezone,
  formatDateTime,
  formatPropertyAddressLines,
  getInspectionStatusDisplay,
  getRegistrationStatusDisplay,
  isDeregisteredCancellationReason,
  shouldShowInspectionStatusOnLicenseList,
} from "@/lib/utils";
import type { JurisdictionLicenses } from "@/types/license";
import { useNavigate } from "@tanstack/react-router";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import * as React from "react";

import {
  REGISTRANT_QUESTION_COLUMN_PREFIX,
  formatRegistrantLicenseAmountDue,
  formatRegistrantLicenseTotalDue,
  getRegistrantLicenseColumnDisplayName,
  registrantColumnAllowsBlankFilter,
  registrantQuestionColumnAccessorKey,
} from "@/components/registrant/dashboard/license-list/license-table";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { listableRegistrantQuestionColumns } from "@/lib/listable-answer-types";

/** When filtered registrations exceed this count, show the operator-style paged table. */
export const REGISTRANT_BULK_LICENSE_THRESHOLD = 25;

/** Stable empty facets — inline `?? {}` breaks memo deps every render. */
export const EMPTY_REGISTRANT_BULK_FACETED_VALUES: Record<string, string[]> =
  {};

/** Total license rows for the selected workspace tab (before per-table column filters). */
export function registrantWorkspaceTabLicenseCount(
  selectedTab: string,
  allActiveRegistrationsJurisdictions: JurisdictionLicenses[],
  balanceDueLicenses: JurisdictionLicenses[],
  renewableLicenses: JurisdictionLicenses[],
  inactiveLicenses: JurisdictionLicenses[],
  needsAttentionLicenses: JurisdictionLicenses[] = [],
): number {
  if (selectedTab === "all") {
    return allActiveRegistrationsJurisdictions.reduce(
      (n, j) => n + j.licenses.length,
      0,
    );
  }
  if (selectedTab === "needs-attention") {
    return needsAttentionLicenses.reduce((n, j) => n + j.licenses.length, 0);
  }
  if (selectedTab === "balance-due") {
    return balanceDueLicenses.reduce((n, j) => n + j.licenses.length, 0);
  }
  if (selectedTab === "renewable") {
    return renewableLicenses.reduce((n, j) => n + j.licenses.length, 0);
  }
  if (selectedTab === "inactive") {
    return inactiveLicenses.reduce((n, j) => n + j.licenses.length, 0);
  }
  return 0;
}

export const REGISTRANT_BULK_STATIC_KEYS = [
  "display_id",
  "property_address",
  "jurisdiction_name",
  "property_apn",
  "county",
  "state",
  "license_type_name",
  "registrant_display_name",
  "status",
  "needs_attention",
  "amount_due",
  "total_due",
  "effective_date",
  "renewal_date",
  "application_date",
  "approval_date",
] as const;

/** Distinct filter values per column for bulk registration filters (matches "(Empty)" convention). */
export function computeRegistrantBulkFacetedValues(
  licenses: RegistrantLicenseListLicenseResponseSchema[],
  accessorKeys: string[],
): Record<string, string[]> {
  const byCol: Record<string, Set<string>> = {};
  for (const k of accessorKeys) {
    byCol[k] = new Set();
  }
  for (const lic of licenses) {
    for (const k of accessorKeys) {
      const raw = bulkFacetRawValue(lic, k);
      const s = typeof raw === "string" ? raw : String(raw ?? "");
      byCol[k].add(!s.trim() ? "(Empty)" : s);
    }
  }
  return Object.fromEntries(
    Object.entries(byCol).map(([key, set]) => [key, Array.from(set).sort()]),
  );
}

function bulkFacetRawValue(
  lic: RegistrantLicenseListLicenseResponseSchema,
  key: string,
): string {
  if (key.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)) {
    const columnKey = key.slice(REGISTRANT_QUESTION_COLUMN_PREFIX.length);
    return lic.question_column_values?.[columnKey] ?? "";
  }
  switch (key) {
    case "display_id":
      return lic.display_id;
    case "jurisdiction_name":
      return lic.jurisdiction.name;
    case "property_address":
      return lic.property
        ? formatPropertyAddressLines(
            lic.property.address1,
            lic.property.address2,
          )
        : "";
    case "property_apn":
      return lic.property?.apn ?? "";
    case "county":
      return lic.jurisdiction.county ?? "";
    case "state":
      return lic.jurisdiction.state ?? "";
    case "license_type_name":
      return lic.license_type.name;
    case "registrant_display_name":
      return lic.registrant_display_name ?? "";
    case "status": {
      const registrationDisplay = getRegistrationStatusDisplay(
        lic.status,
        lic.renewal_date,
        lic.has_awaiting_check_payment,
        lic.cancellation_reason,
        lic.has_awaiting_invoice_payment,
        lic.is_renewable,
        true,
        lic.total_due,
      );
      return registrationDisplay.label;
    }
    case "needs_attention":
      return attentionColumnParts(lic, false).value;
    case "amount_due":
      return formatRegistrantLicenseAmountDue(lic);
    case "total_due":
      return formatRegistrantLicenseTotalDue(lic);
    case "effective_date":
      return lic.effective_date ? formatDateNoTimezone(lic.effective_date) : "";
    case "renewal_date":
      return lic.renewal_date ? formatDateNoTimezone(lic.renewal_date) : "";
    case "application_date":
      return lic.created_at ? formatDateTime(lic.created_at) : "";
    case "approval_date":
      return lic.approved_at ? formatDateTime(lic.approved_at) : "N/A";
    default:
      return "";
  }
}

export function buildRegistrantBulkAccessorKeys(
  showRegistrantColumn: boolean,
  questionColumns: RegistrantLicenseListQuestionColumnSchema[] = [],
): string[] {
  const base = REGISTRANT_BULK_STATIC_KEYS.filter(
    (k) => k !== "registrant_display_name" || showRegistrantColumn,
  );
  const qcol = listableRegistrantQuestionColumns(questionColumns).map(
    (column) => registrantQuestionColumnAccessorKey(column.key),
  );
  return [...base, ...qcol];
}

export function buildRegistrantBulkGetColumnProperties(
  facetedValuesByColumn: Record<string, string[]> | undefined,
  options: { inactiveTab?: boolean },
  questionColumns: RegistrantLicenseListQuestionColumnSchema[] = [],
): (
  key: string,
) => ColumnProperties<RegistrantLicenseListLicenseResponseSchema, string> {
  return (accessorKey: string) => {
    const filterVals = facetedValuesByColumn?.[accessorKey];

    if (accessorKey.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)) {
      const columnKey = accessorKey.slice(
        REGISTRANT_QUESTION_COLUMN_PREFIX.length,
      );
      const column = questionColumns.find((c) => c.key === columnKey);
      return {
        displayName: column?.label ?? columnKey,
        defaultVisible: column?.default_visible ?? false,
        getValue: (row) =>
          row.question_column_values?.[columnKey]?.trim() || "",
        filterValues: filterVals,
        allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
      };
    }

    switch (accessorKey) {
      case "display_id":
        return {
          displayName: "ID #",
          defaultVisible: true,
          getValue: (row) => row.display_id,
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "jurisdiction_name":
        return {
          displayName: "Jurisdiction",
          defaultVisible: true,
          getValue: (row) => row.jurisdiction.name,
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "property_address":
        return {
          displayName: "Address",
          defaultVisible: true,
          getValue: (row) =>
            row.property
              ? formatPropertyAddressLines(
                  row.property.address1,
                  row.property.address2,
                ) || ""
              : "",
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "property_apn":
        return {
          displayName: getRegistrantLicenseColumnDisplayName("property_apn"),
          defaultVisible: false,
          getValue: (row) => row.property?.apn?.trim() || "",
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "county":
        return {
          displayName: "County",
          defaultVisible: true,
          getValue: (row) => row.jurisdiction.county?.trim() || "",
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "state":
        return {
          displayName: "State",
          defaultVisible: true,
          getValue: (row) => row.jurisdiction.state?.trim() || "",
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "license_type_name":
        return {
          displayName: "License Type",
          defaultVisible: true,
          getValue: (row) => row.license_type.name,
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "registrant_display_name":
        return {
          displayName: "Registrant",
          defaultVisible: true,
          getValue: (row) => row.registrant_display_name ?? "",
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "status":
        return {
          displayName: "Status",
          defaultVisible: true,
          getValue: (row) => {
            const registrationDisplay = getRegistrationStatusDisplay(
              row.status,
              row.renewal_date,
              row.has_awaiting_check_payment,
              row.cancellation_reason,
              row.has_awaiting_invoice_payment,
              row.is_renewable,
              true,
              row.total_due,
            );
            const cancellationReason = row.cancellation_reason?.trim();
            if (
              options.inactiveTab &&
              row.status === "CANCELLED" &&
              cancellationReason &&
              Number(row.total_due ?? 0) <= 0
            ) {
              return cancellationReason;
            }
            return registrationDisplay.label;
          },
          renderCell: (row) => {
            const registrationDisplay = getRegistrationStatusDisplay(
              row.status,
              row.renewal_date,
              row.has_awaiting_check_payment,
              row.cancellation_reason,
              row.has_awaiting_invoice_payment,
              row.is_renewable,
              true,
              row.total_due,
            );
            const cancellationReason = row.cancellation_reason?.trim();
            const registrationDisplayLabel =
              options.inactiveTab &&
              row.status === "CANCELLED" &&
              cancellationReason &&
              Number(row.total_due ?? 0) <= 0
                ? cancellationReason
                : registrationDisplay.label;

            const inspectionDisplay = row.newest_inspection_status
              ? getInspectionStatusDisplay(
                  row.newest_inspection_status,
                  row.newest_inspection_has_awaiting_check_payment,
                  row.status !== "PAID_PENDING_REVIEW",
                  row.newest_inspection_has_awaiting_invoice_payment,
                )
              : null;

            const showInspectionStatus =
              inspectionDisplay != null &&
              shouldShowInspectionStatusOnLicenseList(
                row.status,
                registrationDisplay.label,
              );
            const showLicenseStatus = !showInspectionStatus;

            return (
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap gap-2 items-center">
                  {showLicenseStatus && (
                    <Badge variant={registrationDisplay.variant}>
                      {registrationDisplayLabel}
                    </Badge>
                  )}
                  {showInspectionStatus && (
                    <Badge variant={inspectionDisplay.variant}>
                      {inspectionDisplay.label}
                    </Badge>
                  )}
                </div>
                {row.status === "CANCELLED" &&
                  row.cancellation_reason &&
                  !options.inactiveTab &&
                  !isDeregisteredCancellationReason(
                    row.cancellation_reason,
                  ) && (
                    <span className="text-muted-foreground text-xs">
                      {row.cancellation_reason}
                    </span>
                  )}
              </div>
            );
          },
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "needs_attention":
        return {
          displayName: getRegistrantLicenseColumnDisplayName("needs_attention"),
          defaultVisible: true,
          getValue: (row) => attentionColumnParts(row, false).value,
          renderCell: (row) => <AttentionBadges row={row} />,
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "amount_due":
        return {
          displayName: getRegistrantLicenseColumnDisplayName("amount_due"),
          defaultVisible: true,
          getValue: (row) => formatRegistrantLicenseAmountDue(row),
          sortingFn: (rowA, rowB) => {
            const a = Number.parseFloat(rowA.original.due_this_period ?? "0");
            const b = Number.parseFloat(rowB.original.due_this_period ?? "0");
            const na = Number.isFinite(a) ? a : 0;
            const nb = Number.isFinite(b) ? b : 0;
            return na === nb ? 0 : na < nb ? -1 : 1;
          },
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "total_due":
        return {
          displayName: getRegistrantLicenseColumnDisplayName("total_due"),
          defaultVisible: true,
          getValue: (row) => formatRegistrantLicenseTotalDue(row),
          sortingFn: (rowA, rowB) => {
            const a = Number.parseFloat(rowA.original.due_this_period ?? "0");
            const b = Number.parseFloat(rowB.original.due_this_period ?? "0");
            const na = Number.isFinite(a) ? a : 0;
            const nb = Number.isFinite(b) ? b : 0;
            return na === nb ? 0 : na < nb ? -1 : 1;
          },
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "effective_date":
        return {
          displayName: getRegistrantLicenseColumnDisplayName("effective_date"),
          defaultVisible: true,
          getValue: (row) =>
            row.effective_date ? formatDateNoTimezone(row.effective_date) : "",
          sortingFn: (rowA, rowB) =>
            compareDateStrings(
              rowA.original.effective_date,
              rowB.original.effective_date,
            ),
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "renewal_date":
        return {
          displayName: "Renewal Date",
          defaultVisible: true,
          getValue: (row) =>
            row.renewal_date ? formatDateNoTimezone(row.renewal_date) : "",
          sortingFn: (rowA, rowB) =>
            compareDateStrings(
              rowA.original.renewal_date,
              rowB.original.renewal_date,
            ),
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "application_date":
        return {
          displayName: "Application Date",
          defaultVisible: false,
          getValue: (row) =>
            row.created_at ? formatDateTime(row.created_at) : "",
          sortingFn: (rowA, rowB) =>
            compareDateStrings(
              rowA.original.created_at,
              rowB.original.created_at,
            ),
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      case "approval_date":
        return {
          displayName: "Approval Date",
          defaultVisible: false,
          getValue: (row) =>
            row.approved_at ? formatDateTime(row.approved_at) : "N/A",
          sortingFn: (rowA, rowB) =>
            compareDateStrings(
              rowA.original.approved_at,
              rowB.original.approved_at,
            ),
          filterValues: filterVals,
          allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
        };
      default:
        return {
          displayName: accessorKey,
          defaultVisible: false,
          getValue: () => "",
        };
    }
  };
}

interface RegistrantBulkRegistrationsTableProps {
  licenses: RegistrantLicenseListLicenseResponseSchema[];
  questionColumns?: RegistrantLicenseListQuestionColumnSchema[];
  showRegistrantColumn: boolean;
  inactiveTab?: boolean;
  columnFilters: Record<string, string[]>;
  onColumnFiltersChange: (filters: Record<string, string[]>) => void;
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  facetedValuesByColumn: Record<string, string[]>;
  onLicenseRowClick: (licenseId: string) => void;
  serverMode?: boolean;
  rowCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  dataUpdatedAt?: number;
  onFilterPopoverOpen?: (columnId: string, search?: string) => void;
  onServerExport?: (
    mode: "visible" | "all",
    format: "csv" | "excel",
  ) => void | Promise<void>;
  lockToViewport?: boolean;
  leadingContent?: React.ReactNode;
  /** OMT/AMS is view-only; hide Pay/Renew/Cancel actions. */
  hideRowActions?: boolean;
}

export function RegistrantBulkRegistrationsTable({
  licenses,
  questionColumns = [],
  showRegistrantColumn,
  inactiveTab = false,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility,
  onColumnVisibilityChange,
  facetedValuesByColumn,
  onLicenseRowClick,
  serverMode = false,
  rowCount,
  pageIndex,
  pageSize,
  onPaginationChange,
  sorting,
  onSortingChange,
  onRefresh,
  isRefreshing,
  dataUpdatedAt,
  onFilterPopoverOpen,
  onServerExport,
  lockToViewport = false,
  leadingContent,
  hideRowActions = false,
}: RegistrantBulkRegistrationsTableProps) {
  const navigate = useNavigate();
  const { canMakePayments } = useRegistrantOrgPermissions();

  const accessorKeys = React.useMemo(
    () =>
      buildRegistrantBulkAccessorKeys(showRegistrantColumn, questionColumns),
    [showRegistrantColumn, questionColumns],
  );

  const getColumnProperties = React.useMemo(
    () =>
      buildRegistrantBulkGetColumnProperties(
        facetedValuesByColumn,
        { inactiveTab },
        questionColumns,
      ),
    [facetedValuesByColumn, inactiveTab, questionColumns],
  );

  const handlePayClick = React.useCallback(
    (licenseId: string) => {
      navigate({
        to: "/registrant/licenses/$licenseId/payment",
        params: { licenseId },
        // Ignored without the pay grant: that form hides the picker.
        search: { paymentMethod: "card" },
      });
    },
    [navigate],
  );

  const rowActionsColumnWidth = React.useMemo(
    () => licenseRowActionsColumnWidth(licenses),
    [licenses],
  );

  const renderRowActions = React.useCallback(
    (license: RegistrantLicenseListLicenseResponseSchema) => (
      <LicenseRowActions
        license={license}
        userType={UserType.REGISTRANT}
        canMakePayments={canMakePayments}
        onPayClick={handlePayClick}
      />
    ),
    [canMakePayments, handlePayClick],
  );

  return (
    <DashboardTable<RegistrantLicenseListLicenseResponseSchema, string>
      leadingContent={leadingContent}
      data={licenses}
      onRowClick={onLicenseRowClick}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      columnFilters={columnFilters}
      onColumnFiltersChange={onColumnFiltersChange}
      accessorKeys={accessorKeys}
      getColumnProperties={getColumnProperties}
      getRowId={(row) => row.id}
      // Tabs share one layout, matching the shared column visibility state.
      tablePersistenceKey={
        showRegistrantColumn
          ? "registrant-bulk-registrations-with-registrant"
          : "registrant-bulk-registrations"
      }
      initialSortColumn="last_updated_at"
      initialSortDirection="desc"
      renderRowActions={hideRowActions ? undefined : renderRowActions}
      rowActionsColumnWidth={hideRowActions ? undefined : rowActionsColumnWidth}
      serverMode={serverMode}
      lockToViewport={lockToViewport || serverMode}
      rowCount={rowCount}
      pageIndex={pageIndex}
      pageSize={pageSize}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      refreshAriaLabel="Refresh registrations"
      dataUpdatedAt={dataUpdatedAt}
      onFilterPopoverOpen={onFilterPopoverOpen}
      onServerExport={onServerExport}
    />
  );
}
